// Name-announcement recorder: MediaRecorder around getUserMedia, with the
// container picked for the platform (Safari/iOS records audio/mp4; Chrome
// records webm/opus). Exposes a tiny state machine the UI renders.
export type RecorderState = "idle" | "recording" | "done";

function pickMime(): string | undefined {
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!MR?.isTypeSupported) return undefined;
  for (const t of ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]) {
    if (MR.isTypeSupported(t)) return t;
  }
  return undefined;
}

export function extensionFor(mime: string): string {
  if (mime.includes("mp4")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}

export class Recorder {
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private startedAt = 0;

  static supported(): boolean {
    return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && "MediaRecorder" in globalThis;
  }

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const mime = pickMime();
    this.rec = new MediaRecorder(this.stream, mime ? { mimeType: mime } : undefined);
    this.chunks = [];
    this.rec.ondataavailable = (e) => { if (e.data.size) this.chunks.push(e.data); };
    this.rec.start(250);
    this.startedAt = Date.now();
  }

  elapsedSeconds(): number { return this.startedAt ? (Date.now() - this.startedAt) / 1000 : 0; }

  stop(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const rec = this.rec;
      if (!rec) return reject(new Error("not recording"));
      rec.onstop = () => {
        this.stream?.getTracks().forEach((t) => t.stop());
        const type = rec.mimeType || "audio/webm";
        this.rec = null; this.stream = null; this.startedAt = 0;
        resolve(new Blob(this.chunks, { type }));
      };
      rec.stop();
    });
  }

  cancel(): void {
    try { this.rec?.stop(); } catch { /* already stopped */ }
    this.stream?.getTracks().forEach((t) => t.stop());
    this.rec = null; this.stream = null; this.startedAt = 0;
  }
}
